import React, { useEffect, useState } from 'react';
import { apiFetch } from './api';

// Company About + Key Points scraped from screener (company_about table).
// Collapsed it shows the first few lines of About; "More" reveals the rest
// plus Key Points. Shows a placeholder until the scraper has saved one.
const cache = new Map();

// The About row for one stock (null until loaded / when none is saved yet).
// Used by AnalysisPanel to show About inline after the company name.
export const useCompanyAbout = (link) => {
  const [about, setAbout] = useState(() => cache.get(link) || null);
  useEffect(() => {
    if (!link) return undefined;
    let cancelled = false;
    if (cache.has(link)) { setAbout(cache.get(link)); return undefined; }
    setAbout(null);
    apiFetch(`/api/about/${encodeURIComponent(link)}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((row) => { if (row) cache.set(link, row); if (!cancelled) setAbout(row); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [link]);
  return about;
};

const AboutPanel = ({ link }) => {
  const [about, setAbout] = useState(() => cache.get(link) || null);
  const [status, setStatus] = useState(() => (cache.has(link) ? 'done' : 'loading'));
  const [expanded, setExpanded] = useState(() => sessionStorage.getItem('bse5AboutExpanded') === 'on');

  useEffect(() => {
    sessionStorage.setItem('bse5AboutExpanded', expanded ? 'on' : 'off');
  }, [expanded]);

  useEffect(() => {
    if (!link) return;
    let cancelled = false;
    if (cache.has(link)) { setAbout(cache.get(link)); setStatus('done'); return; }
    setAbout(null);
    setStatus('loading');
    apiFetch(`/api/about/${encodeURIComponent(link)}`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((row) => { if (row) cache.set(link, row); if (!cancelled) { setAbout(row); setStatus('done'); } })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [link]);

  if (!about || (!about.about && !about.key_points)) {
    const message = status === 'loading' ? 'Loading…'
      : status === 'error' ? 'About unavailable (restart bse5-backend?)'
        : 'No About saved yet for this stock';
    return (
      <div className="about-panel">
        <div className="about-panel-heading"><h4>About</h4></div>
        <div className="news-panel-empty">{message}</div>
      </div>
    );
  }

  const keyPoints = (about.key_points || '').split('\n').map((line) => line.trim()).filter(Boolean);
  return (
    <div className="about-panel">
      <div className="about-panel-heading">
        <h4>About</h4>
        <div className="about-panel-actions">
          {about.website && <a href={about.website} target="_blank" rel="noreferrer">Website</a>}
          <button type="button" onClick={() => setExpanded((open) => !open)}>{expanded ? 'Less' : 'More'}</button>
        </div>
      </div>
      {about.about && <p className={`about-text${expanded ? '' : ' clamped'}`}>{about.about}</p>}
      {expanded && keyPoints.length > 0 && <>
        <h5>Key Points</h5>
        <ul className="about-key-points">
          {keyPoints.map((point, index) => <li key={index}>{point}</li>)}
        </ul>
      </>}
    </div>
  );
};

export default AboutPanel;

